import { IInfluencerBooking, IInfluencerCards, UserWorkoutSession, WaitlistEntry } from 'fitbeat.gym-core';
import {
  BaseEquipment,
  BaseUserProfile,
  BookingState, EnabledFeatures,
  IEnabledFeatures,
  ILiveStreamOptions,
  StrengthRatioForMuscleGroups, UserProfile,
} from 'fitbeat.models';
import moment from 'moment-timezone';
import { createSelector } from 'reselect';
import { getEnabledFeaturesFromState } from '../auth/auth-selectors';
import { IAppStore } from '../HomeWorkouts';
import { isWeb } from '../utils';
import i18n from '../utils/i18n';

const startTimeASC = (a: UserWorkoutSession, b: UserWorkoutSession) => a.startTime > b.startTime ? 1 : -1;

const getSortedWorkoutSessionsAndInfluencerInvites = (state: IAppStore) => {
  const nonInviteBookings = state.planner.workoutSessions.filter(
    (workoutSession) => workoutSession.bookingState !== BookingState.BookingInvite);

  const userPairedSessionReferences = nonInviteBookings
    .filter((session) => session.pairedSessionReference)
    .map(({ pairedSessionReference }) => pairedSessionReference);

  const unbookedInfluencerInvites = state.planner.influencerBookings.map(({ workoutSession }) => workoutSession)
    .filter((influencerBooking) => !userPairedSessionReferences.includes(influencerBooking.uniqueReference));

  return [...nonInviteBookings, ...unbookedInfluencerInvites].sort(startTimeASC);
};

const getSortedWorkoutInvites = (state: IAppStore) => [...state.planner.workoutSessions]
  .sort(startTimeASC)
  .filter((workoutSession) => workoutSession.bookingState === BookingState.BookingInvite);

export const workoutInvitesSelector = createSelector(
  [getSortedWorkoutInvites],
  (invites) => invites,
);

const getInfluencerWorkoutBookings = (state: IAppStore) => [...state.planner.influencerBookings]
  .filter((influencerBooking) => influencerBooking.workoutSession.bookingState !== BookingState.BookingInvite);

const getWaitlistEntries = (state: IAppStore) => state.planner.waitlistEntries;
const getMarketingTiles = (state: IAppStore) => state.planner.marketingTiles;
const getWorkoutInviteStartTimeForInfluencerBooking = (state: IAppStore) =>
  state.planner.workoutInviteStartTimeForInfluencerBooking;
const getWorkoutInviteIdForInfluencerBooking = (state: IAppStore) =>
  state.planner.influencerWorkoutInviteId;
const getWorkoutInviteIdForGrouInvite = (state: IAppStore) => {
  return state.deepLinkParamaters.workoutInviteId;
};
const getWorkoutInviteStartTimeForGroupInvite = (state: IAppStore) => {
  return state.deepLinkParamaters.workoutInviteStartTime;
};
const getReferredByFullnameForInfluencer = (state: IAppStore) => {
  return state.planner.referredByFullnameForInfluencer;
};
const getReferredByFullnameForGroupInvite = (state: IAppStore) => {
  return state.deepLinkParamaters.referredByFullname;
};

const getStrengthAssessment = (state: IAppStore, userProfile: BaseUserProfile) => {
  return userProfile.strengthRatioForMuscleGroups;
};

export enum TileType {
  workoutSession = 'workoutSession',
  waitListEntry = 'waitListEntry',
  bookWorkout = 'bookWorkout',
  upcomingWorkouts = 'upcomingWorkouts',
  marketingTile = 'marketingTile',
  inviteTile = 'inviteTile',
  strengthAssessmentTile = 'strengthAssessmentTile',
  influencerInviteTile = 'influencerInviteTile',
  bookedInfluencerInviteTile = 'bookedInfluencerInviteTile',
}

export interface IWorkoutTile {
  type: TileType.workoutSession;
  data: UserWorkoutSession;
}

export interface IWaitlistTile {
  type: TileType.waitListEntry;
  data: WaitlistEntry;
}

export interface IMarketingTile {
  type: TileType.marketingTile;
  data: IInfluencerCards;
}

interface IBookWorkoutTile {
  type: TileType.bookWorkout;
}

interface IStrengthAssessmentTile {
  type: TileType.strengthAssessmentTile;
}

interface IUpcomingWorkoutTile {
  type: TileType.upcomingWorkouts;
}

interface IWorkoutInviteTile {
  type: TileType.inviteTile;
  data: UserWorkoutSession;
}

interface IInfluencerInviteTile {
  type: TileType.influencerInviteTile;
  data: IInfluencerBooking;
}

interface IBookedInfluencerInviteTile {
  type: TileType.bookedInfluencerInviteTile;
  data: {
    workoutSession: UserWorkoutSession;
    influencerData: {
      name?: string;
      topic?: string;
    }
  };
}

export type WorkoutSectionTile = IWorkoutTile | IWaitlistTile | IBookWorkoutTile
  | IUpcomingWorkoutTile | IMarketingTile | IWorkoutInviteTile | IStrengthAssessmentTile |
  IInfluencerInviteTile | IBookedInfluencerInviteTile;

export const getWorkoutSectionTiles = createSelector(
  [
    getSortedWorkoutSessionsAndInfluencerInvites,
    getWaitlistEntries,
    getMarketingTiles,
    getSortedWorkoutInvites,
    getStrengthAssessment,
    getInfluencerWorkoutBookings,
    getEnabledFeaturesFromState,
    (_, userProfile) => userProfile,
  ],
  (
    workoutSessionsAndInfluencerInvites: UserWorkoutSession[],
    waitlistEntries: WaitlistEntry[],
    marketingTiles: IInfluencerCards[],
    invites: UserWorkoutSession[],
    strengthRatioForMuscleGroups: StrengthRatioForMuscleGroups | undefined,
    influencerWorkoutBookings: IInfluencerBooking[],
    enabledFeatures: IEnabledFeatures | undefined,
    userProfile,
  ) => {
    const tiles: WorkoutSectionTile[] = [];
    waitlistEntries.map((waitlistEntry) => {
      tiles.push({
        data: waitlistEntry as WaitlistEntry,
        type: TileType.waitListEntry,
      } as IWaitlistTile);
    });

    invites.forEach((invite) => {
      const now = moment();
      const hasInviteExpired = now.isAfter(invite.expiresAt);
      if (!hasInviteExpired) {
        tiles.push({
          data: invite as UserWorkoutSession,
          type: TileType.inviteTile,
        });
      }
    });

    const todaysWorkoutSession = workoutSessionsAndInfluencerInvites.find((workoutSession) =>
      moment(workoutSession.startTime).isSame(moment(), 'days')
      && workoutSession.bookingState === BookingState.ConfirmedBooking);

    const notTodaysWorkouts = workoutSessionsAndInfluencerInvites.filter((workoutSession) =>
      todaysWorkoutSession?.uniqueReference !== workoutSession.uniqueReference);

    if (todaysWorkoutSession) {
      tiles.push(getWorkoutTileTypeForWorkout(todaysWorkoutSession, influencerWorkoutBookings));
    }

    marketingTiles.map((data) => {
      tiles.push({
        data,
        type: TileType.marketingTile,
      });
    });

    notTodaysWorkouts.forEach((workoutSession) => {
      if (workoutSession.bookingState === BookingState.ConfirmedBooking) {
        tiles.push(getWorkoutTileTypeForWorkout(workoutSession, influencerWorkoutBookings));
      }
    });

    tiles.push({
      type: TileType.bookWorkout,
    });

    tiles.push({
      type: TileType.upcomingWorkouts,
    });

    const hasStudioAccess = enabledFeatures && EnabledFeatures.hasAccessToAStudioGym(enabledFeatures);
    const userHasStrengthRatios = !!userProfile.strengthRatioForMuscleGroups;
    const [hasSetDumbbellsAsDefaultEquipment, hasSetBenchBandsAsDefaultEquipment,
    hasSetBenchDumbbellsAsDefaultEquipment] = [
      BaseEquipment.isHomeDumbbellsEquipmentSelection(userProfile.defaultEquipment),
      BaseEquipment.isHomeBenchBandsEquipmentSelection(userProfile.defaultEquipment),
      BaseEquipment.isBenchDumbbellsEquipmentSelection(userProfile.defaultEquipment),
    ];

    const showStrengthAssessmentCard = !isWeb() && (
      hasStudioAccess || hasSetDumbbellsAsDefaultEquipment || hasSetBenchBandsAsDefaultEquipment
      || hasSetBenchDumbbellsAsDefaultEquipment
    );
    const showStrengthAssessmentCardFirst = showStrengthAssessmentCard && !userHasStrengthRatios;

    if (showStrengthAssessmentCard) {
      if (showStrengthAssessmentCardFirst) {
        tiles.unshift({
          type: TileType.strengthAssessmentTile,
        });
      } else {
        tiles.push({
          type: TileType.strengthAssessmentTile,
        });
      }
    }

    return tiles;
  },
);

function getWorkoutTileTypeForWorkout(
  workout: UserWorkoutSession,
  influencerWorkoutBookings: IInfluencerBooking[],
): IWorkoutTile | IInfluencerInviteTile | IBookedInfluencerInviteTile {
  if (workout.pairedSessionReference) {
    const pairedInfluencerBooking = influencerWorkoutBookings.find(
      (influencerBooking) =>
        influencerBooking.workoutSession.uniqueReference === workout.pairedSessionReference);
    if (pairedInfluencerBooking) {
      return {
        type: TileType.bookedInfluencerInviteTile,
        data: {
          workoutSession: {
            ...workout,
            livestreamOptions: pairedInfluencerBooking.workoutSession.livestreamOptions,
          } as UserWorkoutSession,
          influencerData: {
            name: pairedInfluencerBooking.influencerDetails.fullname,
            topic: pairedInfluencerBooking.workoutSession.livestreamOptions?.preWorkoutTalkTopic,
          },
        },
      };
    }
  }
  const influencerInviteBooking = influencerWorkoutBookings.find(
    (influencerBooking) => influencerBooking.workoutSession.uniqueReference === workout.uniqueReference);
  if (influencerInviteBooking) {
    return {
      type: TileType.influencerInviteTile,
      data: influencerInviteBooking,
    };
  }

  const currentUserCreatedStreamingWorkout = workout.isStreamingWorkout && !workout.pairedSessionReference;
  if (currentUserCreatedStreamingWorkout) {
    return {
      type: TileType.bookedInfluencerInviteTile,
      data: {
        workoutSession: workout,
        influencerData: {
          name: i18n.t('dashboard:InfluencerInviteCardMe'),
          topic: workout.livestreamOptions?.preWorkoutTalkTopic,
        },
      },
    };
  }

  return {
    type: TileType.workoutSession,
    data: workout,
  };
}

export const selectWorkoutInviteStartTime = createSelector(
  [getWorkoutInviteStartTimeForGroupInvite, getWorkoutInviteStartTimeForInfluencerBooking],
  (startTimeForGroupInvite, startTimeForInfluencerInvite) => {
    return startTimeForGroupInvite ?? startTimeForInfluencerInvite;
  },
);

export const selectWorkoutInviteIdForInfluencerBooking = createSelector(
  [getWorkoutInviteIdForInfluencerBooking],
  (inviteId) => inviteId,
);

export const selectWorkoutInviteId = createSelector(
  [getWorkoutInviteIdForGrouInvite, getWorkoutInviteIdForInfluencerBooking],
  (groupInviteId, influencerInviteId) => {
    return groupInviteId ?? influencerInviteId;
  },
);

export const selectReferredByFullname = createSelector(
  [getReferredByFullnameForGroupInvite, getReferredByFullnameForInfluencer],
  (groupInviter, influencerInviter) => {
    return groupInviter ?? influencerInviter;
  },
);
